/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, InputPrice, Loading, Datepicker} from '../../../components'
import {KTSVG} from '../../../helpers'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {allCompanyLookup, projectLookup} from '../../../redux/slices/lookup.slice'
import {
  paymentPlanExpenseSaleInvoiceGetId,
  paymentPlanUpdate,
} from '../../../redux/slices/paymentplan.slice'
import {useMemoizedValidationSchema, useMemoizedFormValues, useMemoizedBreadcrumbs} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  loading: state.lookup.loading
})

const selectPaymentPlanData = (state) => ({
  isRedirectPage: state.paymentplan.isRedirectPage,
  detail: state.paymentplan.detail,
  loading: state.paymentplan.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = location.pathname.split('/')

  const {projectLookupList, companyLookup, loading} = useSelector(selectLookupData)
  const {isRedirectPage, detail, loading: updateLoading} = useSelector(selectPaymentPlanData)
  const dispatch = useDispatch()

  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)

  useEffect(() => {
    dispatch(paymentPlanExpenseSaleInvoiceGetId(id))
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    TotalPrice: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    ProjectId: null,
    DefermentDate: new Date(),
    TotalPrice: 0,
    CompanyId: null,
    Description: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(paymentPlanUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)
        for (const values in formValues) {
          if (values === propVal) {
            formikRef.current.setFieldValue(
              propVal,
              property === 'defermentDate' ? new Date(detail[property]) : detail[property]
            )
          }
        }
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.CASH.EXPENSE'}),
      path: '/cash/expense',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/cash/expense/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirectPage) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirectPage, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.CASH.EXPENSE.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.EXPENSE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.PROJECTNAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.DEFERMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('DefermentDate')}
                    onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.TOTALPRICE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('TotalPrice')}
                    onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                  />
                  {formik.touched.TotalPrice && formik.errors.TotalPrice && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.TotalPrice}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.COMPANYNAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={companyLookup}
                    required
                    {...formik.getFieldProps('CompanyId')}
                    onChange={(item) => formik.setFieldValue('CompanyId', Number(item && item.value))}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PAYMENTPLAN.CREATE.DESCRIPTION'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    required
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Description')}
                  />
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
